import {
  fromPairs,
  cloneDeep,
  isEmpty,
  keys,
  mapValues,
  omit,
  reduce,
  set
} from "lodash";
import {
  IFieldAction,
  IFieldMeta,
  IFields,
  IFormState,
  IFormValues,
  TErrors
} from "./formInterfaces";

export const formReset = (state: IFormState) => {
  const { fields, values } = state;

  const resetFields = fromPairs(
    Object.entries(fields).map(([key, value]) => {
      return [
        key,
        {
          required: value.required,
          completed: value.required
            ? ![null, undefined, ""].includes(value.defaultValue)
            : true,
          defaultValue: value.defaultValue
        }
      ];
    })
  );

  const resetValues = cloneDeep(values);
  Object.entries(fields).forEach(([path, meta]) => {
    set(resetValues, path, meta.defaultValue);
  });

  const resetMeta = {
    submitting: false,
    changed: false,
    completed: false
  };
  return {
    values: resetValues,
    fields: resetFields,
    meta: resetMeta
  };
};

export const formRegister = (state: IFormState, action: IFieldAction) => {
  const { fields, values, meta: formMeta } = state;
  const { payload, meta = {} as IFieldMeta, name } = action;

  const nextFeilds = {
    ...fields,
    [name]: {
      ...fields[name],
      ...meta
    }
  };

  const isFormNotCompleted = Object.entries(nextFeilds)
    .map(([key, value]) => !!value.completed)
    .includes(false);

  return {
    fields: nextFeilds,
    meta: {
      ...formMeta,
      completed: !isFormNotCompleted
    },
    values: set<IFormValues>({ ...values }, name, payload)
  };
};

export const formUpdateField = (state: IFormState, action: IFieldAction) => {
  const { fields, values, meta: formMeta } = state;
  const { payload, meta = {} as IFieldMeta, name } = action;

  const nextFeilds = {
    ...fields,
    [name]: {
      ...fields[name],
      ...meta
    }
  };

  const isFormNotCompleted = Object.entries(nextFeilds)
    .map(([key, value]) => !!value.completed)
    .includes(false);

  return {
    fields: nextFeilds,
    meta: {
      ...formMeta,
      changed: true,
      completed: !isFormNotCompleted
    },
    values: set<IFormValues>({ ...values }, name, payload) // Notice: _.set will mutate object,
  };
};

export const formRemoveField = (state: IFormState, action: IFieldAction) => {
  const nextFeilds = omit(state.fields, action.name);
  const isFormNotCompleted = Object.entries(nextFeilds)
    .map(([key, value]) => !!value.completed)
    .includes(false);

  const meta = {
    ...state.meta,
    completed: !isFormNotCompleted
  };
  if (action.meta && action.meta.destroyValueOnUnmount) {
    return {
      meta,
      fields: nextFeilds,
      values: omit(state.values, action.name)
    };
  }

  // keep values when field destroy for cross page form
  // eg: in PageA, switch to PageB, should keep PageA fields values.
  return {
    ...state,
    meta,
    fields: nextFeilds
  };
};

export const formSetErrors = (errors: TErrors, fields: IFields): IFields => {
  if (isEmpty(errors)) {
    return mapValues(fields, field => {
      return {
        ...field,
        error: undefined
      };
    });
  }

  const temp = {} as IFields;
  keys(errors).forEach((name: string) => {
    if (fields[name]) {
      temp[name] = {
        ...fields[name],
        error: errors[name]
      };
    }
  });
  return {
    ...fields,
    ...temp
  };
};

export const isFormValid = (fields: IFields) => {
  return reduce(
    fields,
    (result, item) => result && (item && !item.error),
    true
  );
};
